import { useEffect } from "react";
import { useGitpodAPI } from "@/hooks/use-gitpod-api";
import { agentExecutionEnvironmentIds } from "@/utils/agent-executions";
import { useAuthenticatedUser } from "@/queries/user-queries";
import useIsMobileViewport from "@/hooks/use-is-mobile-viewport";
import { useActiveAgentExecutions } from "@/hooks/use-active-agent-executions";
import { getOrCreateVscodeServer, markEnvironmentResolved } from "@/utils/vscode-server-singleton";

/**
 * Lazily initializes the hidden VS Code server iframe singleton on first call,
 * then monitors active agent executions to resolve new environment IDs.
 *
 * Call from EnvironmentDetailsLayout. The hidden iframe persists across
 * navigation — it is never removed from the DOM.
 */
function useEnsureVscodeServer() {
    const { data: user } = useAuthenticatedUser();
    const { filteredExecutions } = useActiveAgentExecutions();
    const api = useGitpodAPI();
    const { isMobileViewport: isMobile } = useIsMobileViewport();

    // Lazy singleton initialization + agent execution tracking
    useEffect(() => {
        if (isMobile || window.isWebview) return;
        if (!api || !user?.id) return;

        const { ipc } = getOrCreateVscodeServer(api);
        const environmentIds = filteredExecutions.flatMap(({ execution }) =>
            agentExecutionEnvironmentIds(execution),
        );
        for (const envId of environmentIds) {
            if (markEnvironmentResolved(envId)) {
                void ipc.resolve(envId, user.id);
            }
        }
    }, [filteredExecutions, user, isMobile, api]);
}

export { useEnsureVscodeServer };
